import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {maybePluralize} from '@wandb/weave/core/util/string';
import React, {memo, useContext, useMemo} from 'react';
import {Column} from 'react-table';
// Needed to enable a row-hover-based interaction
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';
import {twMerge} from 'tailwind-merge';

import {envIsPublicCloud} from '../../../../config';
import {MimicTable} from '../../../../pages/Team/MimicTable';
import {useViewer} from '../../../../state/viewer/hooks';
import {AccessOption} from '../../../../util/permissions';
import {useRampFlagRegistryObserverRole} from '../../../../util/rampFeatureFlags';
import {checkIsServiceAccount} from '../../../../util/serviceAccounts';
import * as urls from '../../../../util/urls';
import {OrganizationMemberRole} from '../../../MembersTable/util';
import WBReactTable from '../../../WBReactTable';
import {AccountSettingsData} from '../../common/RegistryAccountSettings';
import {globalRegistryType} from '../../common/utils';
import {RegistryMembersContext} from '../RegistryMembersContextProvider';
import {AddRegistryMember} from './AddRegistryMember';
import {useAddRegistryOwnerToMembersTable} from './hooks';
import {RegistryMemberActions} from './RegistryMemberActions';
import {RegistryMemberRoleCell} from './RegistryMemberRoleCell';
import {TeamMembersPreviewTooltip} from './TeamMembersPreviewTooltip';
import {
  isOrganizationRow,
  isTeamRow,
  isUserRow,
  RegistryMemberRow,
  RegistryRoles,
} from './types';
import {RegistryRolePill} from './userRegistryRoleUtils';

// The ProjectMembers query filters these out, but the OrganizationMembersQuery doesn't
// seem to. Since the latter is used in other places too, I'm not blocking these atm,
// Will figure that out in a follow-up PR.
const HiddenServerUserEmails = [
  'local@wandb.com',
  'restore@wandb.com',
  'service@wandb.com',
];

type MemberRow = {
  searchString: string;
  row: RegistryMemberRow;
};

type RegistryMembersTableProps = {
  registry: globalRegistryType;
  accountSettings: AccountSettingsData;
};

export const RegistryMembersTable = memo(
  ({registry, accountSettings}: RegistryMembersTableProps) => {
    const {
      settingsLoading,
      viewerMemberRole,
      isViewerInternalAdmin,
      selectedAccount,
    } = accountSettings;
    const viewer = useViewer();

    const {
      data: enableRestrictedViewerRole,
      loading: enableRestrictedViewerRoleLoading,
    } = useRampFlagRegistryObserverRole(registry.orgName);

    const {
      registryMembers,
      registryTeamMembers,
      organizationMember,
      isRegistryMembersQueryLoading,
      isOrganizationQueryLoading,
      isViewerRegistryAdmin,
    } = useContext(RegistryMembersContext);

    const isViewerOrganizationAdmin =
      viewerMemberRole === OrganizationMemberRole.ADMIN;
    const canViewerEditMember =
      isViewerInternalAdmin ||
      isViewerOrganizationAdmin ||
      isViewerRegistryAdmin;
    const isViewerOwner = viewer?.id === registry?.owner?.id;

    const {isLoading: isAddRegistryOwnerLoading} =
      useAddRegistryOwnerToMembersTable(
        registry,
        registryMembers,
        isRegistryMembersQueryLoading,
        isViewerOwner || canViewerEditMember
      );

    const cellStyle = 'h-full w-full content-center';
    const columns = useMemo(() => {
      const baseColumns: Array<Column<RegistryMemberRow>> = [
        {
          Header: '',
          expander: true,
          Expander: ({isExpanded, row}) => {
            // Only render the toggle for rows with subRows
            if (row._subRows?.length > 0) {
              return (
                <div className="cursor-pointer">
                  {isExpanded ? (
                    <Button icon="chevron-down" variant="ghost" />
                  ) : (
                    <Button icon="chevron-next" variant="ghost" />
                  )}
                </div>
              );
            }
            return null; // Hide the toggle for child rows
          },
          width: 50,
          style: {
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        },
        {
          Header: 'NAME',
          accessor: 'name',
          Cell: ({original}) => {
            const member: RegistryMemberRow = original;
            if (isUserRow(member)) {
              return (
                <div className={cellStyle}>
                  <div className="flex items-center gap-8">
                    {!checkIsServiceAccount(member.accountType) ? (
                      <img
                        src={member.photoUrl ?? '/default-profile-picture.png'}
                        className="h-32 w-32 rounded-full"
                        alt=""
                      />
                    ) : (
                      <div className="content-center justify-center rounded-full bg-teal-300 bg-opacity-[.48] p-6 align-middle">
                        <Icon
                          name="robot-service-member"
                          className="h-20 w-20 text-teal-600"
                        />
                      </div>
                    )}
                    <div>
                      <p className="truncate text-base font-semibold leading-[22px]">
                        {member.name}
                      </p>
                      {member.username && (
                        <TargetBlank
                          className="leading 5 truncate text-base font-semibold text-teal-600"
                          href={urls.profilePage(member.username)}>
                          {member.username}
                        </TargetBlank>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            if (isTeamRow(member)) {
              return (
                <div className={cellStyle}>
                  <div className="flex items-center gap-8">
                    {member.photoUrl != null ? (
                      <img
                        src={member.photoUrl}
                        className="h-32 w-32 rounded-full"
                        alt=""
                      />
                    ) : (
                      <div className="content-center justify-center rounded-full bg-teal-300 bg-opacity-[.48] p-6 align-middle">
                        <Icon
                          name="users-team"
                          className="h-20 w-20 text-teal-600"
                        />
                      </div>
                    )}
                    <div>
                      <p className="truncate text-base font-semibold leading-[22px]">
                        {member.name}
                      </p>
                      <div className="text-base leading-5 text-moon-500">
                        <span>{member.memberType}</span>
                        <span className="font-semibold"> · </span>
                        <TargetBlank
                          className="leading 5 truncate text-base font-semibold text-teal-600"
                          href={urls.teamMembersPage(member.name)}>
                          <TeamMembersPreviewTooltip
                            members={member.teamMembers ?? []}
                            numUsers={member.numUsers}
                          />
                        </TargetBlank>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className={twMerge(cellStyle, 'flex items-center gap-8')}>
                <div className="content-center justify-center rounded-full bg-teal-300 bg-opacity-[0.48] p-6 align-middle">
                  <Icon
                    name="organization-corporate"
                    className="h-20 w-20 text-teal-600"
                  />
                </div>
                <div>
                  <p className="truncate text-base font-semibold leading-[22px]">
                    {envIsPublicCloud ? member.name : 'Organization'}
                  </p>
                  <div className="truncate text-base leading-5 text-moon-500">
                    {envIsPublicCloud && (
                      <>
                        <span>{member.memberType}</span>
                        <span className="font-semibold"> · </span>
                      </>
                    )}
                    <span>{maybePluralize(member.numUsers, 'user')}</span>
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          Header: 'ROLE',
          accessor: 'role',
          Cell: ({original}) => {
            const member: RegistryMemberRow = original;
            if (original?.readOnly || isOrganizationRow(member)) {
              return (
                <div
                  className="flex h-full items-center"
                  data-test="org-role-pill">
                  <RegistryRolePill role={member.role} />
                </div>
              );
            }
            return (
              <div
                className="flex h-full items-center"
                data-test="update-registry-member-role">
                <RegistryMemberRoleCell
                  member={member}
                  registry={registry}
                  isViewerRegistryAdmin={isViewerRegistryAdmin}
                  selectedAccount={selectedAccount}
                  enableRestrictedViewerRole={enableRestrictedViewerRole}
                />
              </div>
            );
          },
        },
        {
          Header: 'EMAIL',
          accessor: 'email',
          Cell: ({original}) => {
            if (original?.readOnly) {
              return null;
            }
            const member: RegistryMemberRow = original;
            if (isUserRow(member)) {
              return (
                <div className="flex h-full items-center">
                  <p className="break-words">{member.email ?? '-'}</p>
                </div>
              );
            }
            return (
              <div className="flex h-full w-full items-center">
                <p className="break-words">-</p>
              </div>
            );
          },
        },
      ];

      if (canViewerEditMember) {
        baseColumns.push({
          Header: ' ',
          sortable: false,
          Cell: ({original}) => {
            if (original?.readOnly) {
              return null;
            }
            const member: RegistryMemberRow = original;
            return (
              <RegistryMemberActions member={member} registry={registry} />
            );
          },
        });
      }

      return baseColumns;
    }, [
      canViewerEditMember,
      isViewerRegistryAdmin,
      registry,
      selectedAccount,
      enableRestrictedViewerRole,
    ]);

    const memberRows: MemberRow[] =
      registryMembers.map(member => {
        const {name, username, email, role, augmentedRole} = member;

        return {
          searchString: `${name}${username}${email}${role}${augmentedRole}`,
          row: {
            ...member,
            displayName: name,
            _subRows: member?.teamRoles?.map(subrow => ({
              ...subrow,
              readOnly: true,
            })),
          },
        };
      }) ?? [];

    registryTeamMembers.forEach(member => {
      const {name, role} = member;
      memberRows.push({
        searchString: `${name}${role}`,
        row: {...member},
      });
    });
    if (
      settingsLoading ||
      isRegistryMembersQueryLoading ||
      isOrganizationQueryLoading ||
      isAddRegistryOwnerLoading ||
      enableRestrictedViewerRoleLoading
    ) {
      return <MimicTable columnNames={['NAME', 'ROLE', 'EMAIL']} numRows={5} />;
    }

    if (registry.access === AccessOption.Private) {
      memberRows.push({
        searchString: `${registry.orgName}${RegistryRoles.Viewer}`,
        row: organizationMember,
      });
    }
    return (
      <Tailwind>
        <div className="flex w-full items-center justify-between pt-8">
          <div className="text-xl font-semibold leading-8">Registry access</div>
          {canViewerEditMember && <AddRegistryMember registry={registry} />}
        </div>
        <StyledTableWithRowHover
          className="mt-16 rounded-lg border-moon-250"
          data-test="organization-members-table"
          columns={columns}
          data={memberRows}
          defaultSorted={[{id: 'memberType', desc: true}]}
          pageSize={10}
          getTrProps={() => ({
            style: {
              height: '60px',
            },
          })}
        />
      </Tailwind>
    );
  }
);

// Adding this to have the actions appear on row hover without using
// state variables
const StyledTableWithRowHover = styled(WBReactTable)`
  .rt-tr-group {
    .registry-actions-container {
      visibility: hidden;
    }
    &:hover .registry-actions-container {
      visibility: visible;
    }
  }
`;
StyledTableWithRowHover.displayName = 'S.StyledTableWithRowHover';
